// Microsoft Entra ID, the provider in dev and local (and, after cutover, in
// every environment).
//
// Sign-in goes straight to Microsoft's hosted pages; the Engage /login page is
// never used. Sessions last as long as the refresh token (up to 24h for an SPA)
// and Conditional Access allow. Members match ../forgerock/provider.js.

import { AUTH_ROUTE_PATHS } from "../../authRoutePaths";
import {
  clearAccountSelectionRequired,
  isAccountSelectionRequired,
  markAccountSelectionRequired,
} from "./accountSelection";
import {
  clearCallbackFailures,
  recordCallbackFailure,
  shouldStopRestartingSignIn,
} from "./callbackFailureGuard";
import { clearDomainHint, getDomainHint } from "./domainHint";

export const createEntraProvider = (settings) => ({
  id: "entra",
  settings,

  // --- Discovery and client registration ---------------------------------

  wellKnownUrl: settings.wellKnownUrl,
  scope: settings.scope,
  // One app registration serves the browser and the app's WebView.
  clientId: () => settings.clientId,

  // --- UserManager ---------------------------------------------------------

  // Microsoft's own authorize endpoint, from discovery.
  authorizationEndpoint: undefined,
  // Entra codes run ~2000 chars, and the CloudFront WAF refuses query strings
  // over 2048 bytes, so /auth/callback?code=... would 403 at the edge. The
  // fragment never reaches a server (or its access logs). This one setting
  // both requests fragment mode and makes oidc-client parse after "#";
  // EntraCallback reads location.hash to match.
  responseMode: "fragment",

  // --- Tokens --------------------------------------------------------------

  // See entraAuth in appsettings: the ID token until the app registration
  // exposes an API scope.
  apiBearer: settings.apiBearer ?? "id_token",

  // An expired access token is not an expired session: the refresh token
  // outlives it by up to 24h, so renew before sending the user back through
  // Microsoft.
  renewsExpiredSessions: true,

  // Renewal is unconditional: a refresh-token grant never touches the
  // provider's browser session, so Conditional Access and the 24h SPA
  // refresh-token cap bound an idle tab. Nothing reads user activity.
  tracksUserActivity: false,
  shouldRenewInBackground: () => true,

  // Entra v2.0 has no revocation endpoint and its access tokens are
  // short-lived bearer JWTs that can't be revoked; end-session ends the
  // session.
  revokesTokensOnLogout: false,

  // --- Sign-in -------------------------------------------------------------

  hasInAppLoginPage: false,
  loginPageUrl: undefined,

  // prompt=select_account after a manual logout (see ./accountSelection),
  // and domain_hint to send distributor users straight to their own IdP (see
  // ./domainHint). There is no session-expired banner to forward to.
  signinParams: () => {
    const domainHint = getDomainHint();
    return {
      ...(isAccountSelectionRequired() && { prompt: "select_account" }),
      ...(domainHint && { domain_hint: domainHint }),
    };
  },

  // Disarm the account-picker flag when a flow gives up on signing in.
  // Nothing downstream would complete a sign-in, so leaving it armed would put
  // an unexplained prompt=select_account on whatever sign-in comes next.
  abandonSignin: () => clearAccountSelectionRequired(),

  // Account expired: there is no /login page, so it lands on the signed-out
  // screen, which shows the reason and waits for a deliberate retry.
  blockedPath: AUTH_ROUTE_PATHS.LOGOUT_CALLBACK,

  // A restart completes silently against the live Microsoft session, so a
  // failure that persists would loop; stop and ask instead.
  shouldStopRestartingSignin: () =>
    shouldStopRestartingSignIn(recordCallbackFailure()),

  // A completed sign-in disarms the per-attempt flags above.
  onSigninCompleted: () => {
    clearCallbackFailures();
    clearAccountSelectionRequired();
  },

  // Drop the stored federated realm. A refusal is the best evidence we get
  // that the hint routed the user to an IdP that does not know them, the
  // likeliest cause being a shared workstation still carrying the previous
  // user's distributor. Clearing it makes the next sign-in fall back to
  // Microsoft's home-realm discovery, which always works, so the wrong realm
  // self-heals instead of needing a support call. Harmless when the refusal
  // had some other cause: the user enters their email once.
  onProviderRefusal: () => clearDomainHint(),

  // --- Sign-out ------------------------------------------------------------

  // v2.0 end-session has no per-app scope, so this also signs the browser out
  // of other Microsoft apps. Kept because Engage runs on shared workstations,
  // where a surviving tenant session is worse.
  //
  // logout_hint (from the login_hint optional claim) can skip Entra's "which
  // account?" prompt, but Entra reports nothing back, so a manual logout always
  // forces the account picker on the next sign-in. Armed here because only
  // a manual logout reaches it.
  beforeSignout: () => markAccountSelectionRequired(),

  // client_id is needed for Entra to honor post_logout_redirect_uri, and
  // oidc-client v1 does not send it. id_token_hint is ignored.
  signoutParams: ({ logoutHint }) => ({
    extraQueryParams: {
      client_id: settings.clientId,
      ...(logoutHint && { logout_hint: logoutHint }),
    },
  }),

  // --- Routes ----------------------------------------------------------------

  // Only old links reach /login; see EntraLoginRedirect.
  loadLoginPage: () => import("@/components/auth/EntraLoginRedirect"),
  // The authorization response arrives in the fragment (see responseMode),
  // which PageLogin doesn't read.
  loadCallbackPage: () => import("@/components/auth/EntraCallback"),
});
